import {AutomationChoose} from '../../../../../reducer/model';
import {getLang, isRTL} from '../../../../../../../Localization';
import {BusinessRuleAction, messageLangs} from '../../../../../constants';
import {RecipientsType} from './enum';
import {RuleType} from '../../../../../enums';
import {
    ENGLISH_INSTALLATION_LANGUAGE,
    HEBREW_INSTALLATION_LANGUAGE,
    languageTabs
} from '../../AutomationActionToolbar/constants';
import {priority} from '@priority-software/priority-proto-api/lib/nform';
import {TAlertMessageDictionary, TDictionaryLangKey} from '../../../../../types';
import {TEditorTemplatesDictionary} from '../../../../../../EmailEditor/reducer/types';
import {getBasicLanguage} from '../../../../../../../_shared/GWTBridge/invoke/common';

export const prepareFieldOptions = (
    fields: AutomationChoose[],
    englishFields: AutomationChoose[]
) => {
    // Prepare the options for the field selection dropdown according to the language
    return {
        1: fields.map(field => {
            const englishField = englishFields.find(item => item.value === field.value);
            return {
                label: field.label,
                description: isRTL() ? '' : englishField?.label ? `<<${englishField?.label}>>` : '',
                value: englishField?.label || field.label
            };
        }),
        3: englishFields.map(field => {
            return {
                label: field.description,
                description: field.label ? `<<${field.label}>>` : '',
                value: field.label
            };
        })
    };
};

export const isMessageTemplate = (Messages: TAlertMessageDictionary) => {
    return Object.values(Messages).some(message => !!message.Message);
};

export const getActiveMessageLang = (): string => {
    const curLang: string = getLang();
    const langTab = languageTabs.find(t => t.id === curLang) || languageTabs[0];
    return langTab.id as TDictionaryLangKey;
};

export const getActiveSubject = (CoverLetters: TAlertMessageDictionary): string => {
    const initTabLangID = getActiveMessageLang();

    // Try to get the subject from the CoverLetters dictionary using the initial language key
    // if the Template was created with the old email editor, we added default value from Messages
    // field during data mapping in the useBusinessRuleTemplate hook
    const subject = CoverLetters[initTabLangID]?.Subject;

    if (subject) return subject;

    for (const langValue of Object.keys(messageLangs)) {
        const fallbackSubject = CoverLetters[langValue]?.Subject;
        if (fallbackSubject) return fallbackSubject;
    }
    return '';
};

export const getSupportedRecipients = (ruleType: RuleType, action: BusinessRuleAction) => {
    switch (action) {
        case BusinessRuleAction.TextMsg:
            return [
                RecipientsType.user,
                RecipientsType.group,
                RecipientsType.field,
                RecipientsType.phone
            ];
        case BusinessRuleAction.EmailMsg:
            return [
                RecipientsType.user,
                RecipientsType.group,
                RecipientsType.email,
                RecipientsType.field
            ];
        case BusinessRuleAction.PushNotification:
            return [RecipientsType.user, RecipientsType.group, RecipientsType.field];
        case BusinessRuleAction.AllowStatusChange:
            return [RecipientsType.user, RecipientsType.group, RecipientsType.field];
        case BusinessRuleAction.OpenTask:
            return ruleType === RuleType.bpm
                ? [RecipientsType.user, RecipientsType.field]
                : [RecipientsType.user];
        case BusinessRuleAction.AssignTo:
            return [RecipientsType.user, RecipientsType.group, RecipientsType.field];
        default:
            return [];
    }
};

export const createMessageDictionary = (
    data: priority.netitems.form.IAlertMessage[]
): TAlertMessageDictionary => {
    return data.reduce<TAlertMessageDictionary>((prev, curr) => {
        const langID = curr.Language.toString() as TDictionaryLangKey;
        return {...prev, [langID]: curr} satisfies TAlertMessageDictionary;
    }, {});
};

export const convertEditorTemplatesToDictionary = (tempTemplates: TEditorTemplatesDictionary) => {
    return languageTabs.reduce((acc, lang) => {
        const language = lang.id satisfies TDictionaryLangKey;
        const Subject = tempTemplates?.[lang.id]?.editorSubjectIds;
        const Message = tempTemplates?.[lang.id]?.emailHtml;

        return {
            ...acc,
            [language]: {
                Language: parseInt(language),
                Subject,
                Message
            }
        } satisfies TAlertMessageDictionary;
    }, {});
};

export const shouldShowTabs = (
    Messages: Partial<TAlertMessageDictionary>,
    automationType: RuleType
) => {
    // show english and hebrew tabs in English environment if there are messages in hebrew language (EP-54683)
    const basicLanguage = getBasicLanguage(); // system installation language
    const isBpm = automationType === RuleType.bpm;
    const shouldShowTabs = isBpm
        ? Messages?.[HEBREW_INSTALLATION_LANGUAGE]?.Message?.length > 0 ||
          Messages?.[HEBREW_INSTALLATION_LANGUAGE]?.Subject?.length > 0
        : Object.keys(Messages)?.includes(HEBREW_INSTALLATION_LANGUAGE.toString());

    return basicLanguage !== ENGLISH_INSTALLATION_LANGUAGE || shouldShowTabs;
};
